import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Alert, Button, Spin, Steps } from 'antd'
import { LeftOutlined } from '@ant-design/icons'
import { useAuthSession } from '@/lib/session'
import { AdvisoryCallStatus } from '@/features/advisory-ai/types'
import { useAdvisoryAiOrgDetails, useAdvisoryAiReport } from '@/features/advisory-ai/hooks'
import { isSampleCall } from '@/features/advisory-ai/sampleCall'
import { isRealMeetingDate } from '@/features/advisory-ai/calls/utils'
import { ADVISORY_ACCESS_DENIED_MESSAGE, isAdvisoryAccessDenied } from '@/features/advisory-ai/access'
import FeatureThemeProvider from '@/components/FeatureThemeProvider'
import { useAssistant } from '@/features/advisory-ai/assistant'
import { AgendaStep, DataStep, ReportStep, GenerateOutputsStep } from './components'
import { parseMeetingStepParam, STEP_AGENDA, STEP_DATA, STEP_GENERATE_OUTPUTS, STEP_REPORT } from './steps'
import dayjs from 'dayjs'

function getInitialStep(status?: string | null): number {
  const s = String(status || '').toUpperCase()
  if (s === AdvisoryCallStatus.PREPARE_EXPORTING || s === AdvisoryCallStatus.EXPORTING) return STEP_GENERATE_OUTPUTS
  // Only a brand-new (not-yet-analysed) report starts on the agenda.
  if (s === '' || s === AdvisoryCallStatus.PENDING) return STEP_AGENDA
  // Analysed / under review / chat-in-progress / errored / exported → the review step.
  return STEP_REPORT
}

export default function AdvisoryAiCallPage() {
  const { orgId = '', callId = '' } = useParams<{ orgId: string; callId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const session = useAuthSession()
  const clientId = session?.currentClientId || ''
  const [currentStep, setCurrentStep] = useState(0)
  const [findingsUpdating, setFindingsUpdating] = useState(false)
  const [pendingExportsRefresh, setPendingExportsRefresh] = useState(false)
  const initialStepSet = useRef(false)
  const sawFetchingAfterUpdate = useRef(false)

  const markFindingsChanged = useCallback(() => {
    setFindingsUpdating(true)
    setPendingExportsRefresh(true)
  }, [])

  // Preppy is mounted above the router outlet, so it publishes rather than
  // calls: a walkthrough asks for a step and the report chat reports that it
  // changed the findings, and this page applies both.
  const { state: assistant, actions: assistantActions } = useAssistant()
  const assistantStepRequest = assistant.requestedStep
  const findingsUpdatedToken = assistant.findingsUpdatedToken
  const { reportWizardStep } = assistantActions

  // ...and the traffic runs both ways: an interactive walkthrough waits for the
  // user to press Continue, which it can only know about if the wizard says so.
  // Reporting the real step also means the walkthrough never has to re-implement
  // this page's validation — required fields are simply why the step has not
  // moved yet.
  useEffect(() => {
    reportWizardStep(currentStep)
    return () => reportWizardStep(null)
  }, [currentStep, reportWizardStep])

  useEffect(() => {
    if (!assistantStepRequest) return
    setCurrentStep(Math.max(STEP_AGENDA, Math.min(STEP_GENERATE_OUTPUTS, assistantStepRequest.step)))
    // Claim the initial-step slot: that effect runs later, when the report
    // status resolves, and would otherwise overwrite this.
    initialStepSet.current = true
  }, [assistantStepRequest])

  useEffect(() => {
    if (findingsUpdatedToken === 0) return
    markFindingsChanged()
  }, [findingsUpdatedToken, markFindingsChanged])

  const orgQuery = useAdvisoryAiOrgDetails(orgId, clientId)
  const reportQuery = useAdvisoryAiReport(callId, clientId, true)

  useEffect(() => {
    if (!findingsUpdating) {
      sawFetchingAfterUpdate.current = false
      return
    }
    if (reportQuery.isFetching) {
      sawFetchingAfterUpdate.current = true
    } else if (sawFetchingAfterUpdate.current) {
      setFindingsUpdating(false)
      sawFetchingAfterUpdate.current = false
    }
  }, [findingsUpdating, reportQuery.isFetching])
  const requestedStepIndex = parseMeetingStepParam(searchParams.get('step'))

  /**
   * A pop-up can point at a step while this page is already open, which changes
   * the search param without remounting — so the param is applied whenever it
   * appears, not only when the report status first resolves. Consuming it keeps
   * a later refresh from re-pinning the step you have since moved off.
   */
  useEffect(() => {
    if (requestedStepIndex === null) return
    setCurrentStep(requestedStepIndex)
    initialStepSet.current = true
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('step')
        return next
      },
      { replace: true }
    )
  }, [requestedStepIndex, setSearchParams])

  /**
   * Reset during render, not in an effect. As an effect it ran *after* the
   * step-request effect above — declaration order — clearing the claim on the
   * same commit that made it.
   */
  const lastCallId = useRef(callId)
  if (lastCallId.current !== callId) {
    lastCallId.current = callId
    initialStepSet.current = false
  }

  useEffect(() => {
    if (initialStepSet.current || !reportQuery.data?.status) return
    initialStepSet.current = true
    setCurrentStep(getInitialStep(reportQuery.data.status))
  }, [callId, reportQuery.data?.status])

  const steps = [
    { title: 'Agenda' },
    { title: 'Data' },
    { title: 'AI Analysis & Review' },
    { title: 'Generate Outputs' },
  ]

  const getCompanyInitial = (name: string) => name.charAt(0).toUpperCase()
  const orgName = orgQuery.data?.name || 'Company'

  if (!clientId) {
    return <Alert type="warning" showIcon message="No active client selected" />
  }

  // Someone who followed a link to a meeting whose organisation they are not on. Answer it
  // plainly rather than leaving them on a generic "failed to load".
  if (isAdvisoryAccessDenied(reportQuery.error) || isAdvisoryAccessDenied(orgQuery.error)) {
    return (
      <FeatureThemeProvider featureKey="advisoryIntel" withAntd className="w-full max-w-6xl mx-auto">
        <Alert
          type="warning"
          showIcon
          message="Access restricted"
          description={ADVISORY_ACCESS_DENIED_MESSAGE}
          action={
            <Button icon={<LeftOutlined />} onClick={() => navigate(`/adviceprep/org/${orgId}/calls`)}>
              Back to meetings
            </Button>
          }
        />
      </FeatureThemeProvider>
    )
  }

  if (orgQuery.isLoading || (reportQuery.isLoading && !reportQuery.data)) {
    return (
      <FeatureThemeProvider featureKey="advisoryIntel" withAntd className="flex items-center justify-center h-64">
        <Spin size="large" />
      </FeatureThemeProvider>
    )
  }

  if (orgQuery.error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Failed to load organisation"
        description={orgQuery.error instanceof Error ? orgQuery.error.message : 'Unknown error'}
      />
    )
  }

  if (!orgQuery.data) {
    return <Alert type="warning" showIcon message="Organisation not found" />
  }

  return (
    <FeatureThemeProvider
      featureKey="advisoryIntel"
      withAntd
      className="w-full max-w-6xl mx-auto"
    >
      {isSampleCall(callId) && (
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message="Sample prep"
          description="Example contents, so you can see how a prep is put together. Nothing here is saved, and none of it belongs to this organisation — create a meeting to run a real one."
        />
      )}

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full feature-soft-surface flex items-center justify-center">
              <span className="text-xl font-bold feature-soft-text">{getCompanyInitial(orgQuery.data.name)}</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{orgQuery.data.name} — Advisory Call</h1>
              {reportQuery.data?.createdAt && (
                <p className="text-sm text-gray-600">
                  {dayjs(reportQuery.data.createdAt).format('dddd D MMM YYYY - h:mm A')}
                </p>
              )}
              {isRealMeetingDate(reportQuery.data?.meetingDate) && (
                <p className="text-sm text-gray-600" data-testid="call-meeting-date">
                  Meeting: {dayjs(reportQuery.data?.meetingDate).format('dddd D MMM YYYY - h:mm A')}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button icon={<LeftOutlined />} onClick={() => navigate(`/adviceprep/org/${orgId}/calls`)}>
              ← All meetings
            </Button>
          </div>
        </div>

        <div className="mb-8" data-tour="stepper">
          <Steps current={currentStep} items={steps.map((s) => ({ title: s.title }))} />
        </div>
      </div>

      <div data-tour="step-body" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        {currentStep === STEP_AGENDA && (
          <AgendaStep
            callId={callId}
            clientId={clientId}
            orgId={orgId}
            onContinue={() => setCurrentStep(STEP_DATA)}
          />
        )}
        {currentStep === STEP_DATA && (
          <DataStep
            orgId={orgId}
            callId={callId}
            clientId={clientId}
            orgName={orgName}
            onBack={() => setCurrentStep(STEP_AGENDA)}
            onContinue={() => setCurrentStep(STEP_REPORT)}
          />
        )}
        {currentStep === STEP_REPORT && (
          <ReportStep
            callId={callId}
            clientId={clientId}
            findingsUpdating={findingsUpdating}
            executiveSummaryAsAgenda
            onBack={() => setCurrentStep(STEP_DATA)}
            onFinish={() => setCurrentStep(STEP_GENERATE_OUTPUTS)}
          />
        )}
        {currentStep === STEP_GENERATE_OUTPUTS && (
          <GenerateOutputsStep
            orgId={orgId}
            callId={callId}
            clientId={clientId}
            onBack={() => setCurrentStep(STEP_REPORT)}
            pendingExportsRefresh={pendingExportsRefresh}
            onExportsRefreshed={() => setPendingExportsRefresh(false)}
          />
        )}
      </div>

    </FeatureThemeProvider>
  )
}
